import {
  getOutcomeNoun,
  isRevenueOutcomeDefinition,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import type {
  PlaybookCriterionNode,
  PlaybookId,
} from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { GradingId } from "@repo/app-types/entities/teammatePerformanceSummary";
import {
  computeTeammatePlaybookCoachingDates,
  type CoachingContentV3,
  type CriterionCoachingContent,
  type CriterionCoachingScoreTargetGoal,
  type TeammatePlaybookCoaching,
} from "@repo/app-types/entities/teammatePlaybookCoaching";
import {
  computeCoachingGoalDayProgress,
  computeCoachingGoalExpectedWindowCalls,
  computeCoachingGoalScorePath,
  getCoachingGoalStatus,
} from "@repo/app-types/helpers/coachingGoalProgress";
import { getOutcomeImpactCopy } from "@repo/app-types/helpers/outcomeImpactCopy";
import { renderPlural } from "@repo/app-types/helpers/utilities";
import type { CoachingGoalCallPoint } from "@repo/ui/coachingNote/coachingGoalTypes";
import { WeeklyCoachingGoalCard } from "@repo/ui/coachingNote/weeklyCoachingGoalCard";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { DateTime } from "luxon";
import { useCallback, useMemo, type FC, type ReactNode } from "react";
import { usePlaybookTeammateCoachingGoalCallsQuery } from "../../api/entities/performancePlaybook.js";
import { appPaths } from "../../app/appPaths.js";
import { useCoachingAnalytics } from "./coachingAnalytics.js";

/** Coaching content narrowed to the single-focus score-target case the card serves. */
type ScoreTargetCoachingContent = CriterionCoachingContent & {
  content: CoachingContentV3;
  goal: CriterionCoachingScoreTargetGoal;
  /** The snapshotted baseline; the card requires a concrete started score. */
  teammate_average_score: number;
};

interface WeeklyCoachingGoalCardContainerProps {
  playbookId: PlaybookId;
  teammateId: TeammateId;
  coaching: TeammatePlaybookCoaching;
  criterion: PlaybookCriterionNode;
  criterionContent: ScoreTargetCoachingContent;
  outcomeDefinition: OutcomeDefinition | undefined;
  periodSelector: ReactNode;
  /** Optional manager note (or its editor), rendered above the AI note body. */
  managerNoteSlot?: ReactNode;
  /** Keeps the AI note body expanded (e.g. while the manager note editor is open). */
  isExpansionForced?: boolean;
}

/**
 * Assembles the data-shaped props of {@link WeeklyCoachingGoalCard} from the
 * stored coaching row and the goal-window call scores. Score path, day progress,
 * and status copy come from the shared derivations in app-types so the card, the
 * achievement pill, and any future surface agree.
 */
export const WeeklyCoachingGoalCardContainer: FC<
  WeeklyCoachingGoalCardContainerProps
> = (props) => {
  const {
    playbookId,
    teammateId,
    coaching,
    criterion,
    criterionContent,
    outcomeDefinition,
    periodSelector,
    managerNoteSlot,
    isExpansionForced,
  } = props;

  const now = useNow();

  const { currentData: goalCalls, isFetching: isCallsLoading } =
    usePlaybookTeammateCoachingGoalCallsQuery({
      playbookId,
      teammateId,
      periodId: coaching.period_id,
    });

  const dates = useMemo(
    () => computeTeammatePlaybookCoachingDates(coaching),
    [coaching],
  );

  const calls = useMemo<CoachingGoalCallPoint[]>(
    () =>
      (goalCalls?.calls ?? []).map((call) => ({
        id: call.grading_id,
        customer: call.customer_name,
        email: call.customer_secondary ?? "",
        recordedAtLabel: DateTime.fromISO(call.recorded_at).toFormat("MMM d"),
        callSummaryLabel: buildCallSummaryLabel(
          call.call_count,
          call.duration_seconds,
        ),
        score: call.score,
        href: appPaths.teammateCall.build({
          playbookId,
          teammateId,
          gradingId: call.grading_id,
        }),
      })),
    [goalCalls, playbookId, teammateId],
  );

  const startedScore = criterionContent.teammate_average_score;
  const targetScore = criterionContent.goal.score_target;
  const gradingValueType = criterion.grading_value_type;

  const dayProgress = computeCoachingGoalDayProgress(dates, now);
  // The plotted path is the blended (shrinkage) score: the window's calls
  // averaged with a phantom observation of the starting score whose weight is
  // the scored calls the rest of the window is expected to produce, estimated
  // from the trailing scored-call volume the endpoint reports. Early calls
  // barely move the needle; by the deadline the blend equals the plain mean
  // the goal is evaluated on.
  const expectedWindowCalls = computeCoachingGoalExpectedWindowCalls({
    trailingScoredCallCount: goalCalls?.trailing_scored_call_count ?? 0,
    windowDays: dates.goalEvaluationWindowEnd
      .diff(dates.goalEvaluationWindowStart)
      .as("days"),
  });
  const scorePath = useMemo(
    () =>
      computeCoachingGoalScorePath(
        (goalCalls?.calls ?? []).map((call) => ({
          score: call.score,
          elapsedFraction: computeCoachingGoalDayProgress(
            dates,
            DateTime.fromISO(call.recorded_at),
          ),
        })),
        {
          startedScore,
          expectedWindowCalls,
          currentElapsedFraction: dayProgress,
        },
      ),
    [goalCalls, dates, startedScore, expectedWindowCalls, dayProgress],
  );
  const status = getCoachingGoalStatus({
    currentScore: scorePath.at(-1) ?? null,
    startedScore,
    targetScore,
    gradingValueType,
    dayProgress,
    periodId: coaching.period_id,
  });

  const netConversions = criterionContent.net_conversions ?? 0;
  const impactLabel = buildImpactLabel(netConversions, outcomeDefinition);

  const { captureCallOpen, captureNoteExpand } = useCoachingAnalytics();

  const dateRange = {
    length: dates.goalEvaluationWindowEnd.diff(dates.goalEvaluationWindowStart),
    end: dates.goalEvaluationWindowEnd,
  };

  const handleCallOpen = useCallback(
    (gradingId: GradingId) => {
      captureCallOpen({ gradingId });
    },
    [captureCallOpen],
  );
  const handleExpandedChange = useCallback(
    (isExpanded: boolean) => {
      if (isExpanded) {
        captureNoteExpand();
      }
    },
    [captureNoteExpand],
  );

  return (
    <WeeklyCoachingGoalCard
      dateRange={dateRange}
      periodSelector={periodSelector}
      criterionName={criterion.name}
      criterionInfo={criterion.grading_context || criterion.description}
      impactLabel={impactLabel}
      startedScore={startedScore}
      targetScore={targetScore}
      gradingValueType={gradingValueType}
      scorePath={scorePath}
      dayProgress={dayProgress}
      statusLabel={status.label}
      statusTone={status.tone}
      managerNoteSlot={managerNoteSlot}
      isExpansionForced={isExpansionForced}
      content={criterionContent.content}
      renderContext={{ teammateId, playbookId, criterionId: criterion.id }}
      calls={calls}
      isChartLoading={isCallsLoading}
      onCallOpen={handleCallOpen}
      onExpandedChange={handleExpandedChange}
    />
  );
};

function buildCallSummaryLabel(
  callCount: number,
  durationSeconds: number,
): string {
  const callLabel = renderPlural(callCount, {
    one: "1 call",
    other: (count) => `${count} calls`,
  });
  const minutes = Math.max(1, Math.round(durationSeconds / 60));
  return `${callLabel} (${minutes}m)`;
}

function buildImpactLabel(
  netConversions: number,
  outcomeDefinition: OutcomeDefinition | undefined,
): string {
  if (netConversions <= 0) {
    return "";
  }

  const impactCopy = getOutcomeImpactCopy({
    netConversions,
    averageRevenuePerOpp: undefined,
    outcomeNoun: outcomeDefinition
      ? getOutcomeNoun(outcomeDefinition)
      : undefined,
    outcomePluralNoun: outcomeDefinition?.plural_noun,
    isRevenueOutcome: outcomeDefinition
      ? isRevenueOutcomeDefinition(outcomeDefinition)
      : undefined,
  });

  if (impactCopy.isRevenue) {
    return impactCopy.pillLabel;
  }
  return `~${Math.round(netConversions).toLocaleString("en-US")} more ${impactCopy.pluralNoun}/1K calls`;
}
